import React from 'react';
import { reportError } from '../utils/errorHandling.js';

function VolunteerApplication({ onBackClick }) {
    try {
        const [formData, setFormData] = React.useState({
            name: '',
            email: '',
            phone: '',
            role: '',
            skills: '',
            experience: '',
            availability: '',
            motivation: ''
        });
        const [isSubmitting, setIsSubmitting] = React.useState(false);
        const [submitStatus, setSubmitStatus] = React.useState(null);

        const volunteerRoles = [
            { value: 'technical', label: 'Technical Volunteer' },
            { value: 'education', label: 'Education Volunteer' },
            { value: 'outreach', label: 'Community Outreach' },
            { value: 'admin', label: 'Administrative Support' },
            { value: 'other', label: 'Other' }
        ];

        const handleSubmit = async (e) => {
            e.preventDefault();
            setIsSubmitting(true);
            setSubmitStatus(null);

            try {
                // Save using volunteer service with Supabase
                await window.volunteerService.createVolunteerApplication({
                    ...formData,
                });

                setSubmitStatus('success');
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    role: '',
                    skills: '',
                    experience: '',
                    availability: '',
                    motivation: ''
                });
            } catch (error) {
                console.error('Volunteer application error:', error);
                setSubmitStatus('error');
                reportError(error);
            } finally {
                setIsSubmitting(false);
            }
        };

        const handleChange = (e) => {
            setFormData({
                ...formData,
                [e.target.name]: e.target.value
            });
        };

        const handleBackClick = (e) => {
            e.preventDefault();
            if (onBackClick) onBackClick();
        };

        return (
            <section data-name="volunteer-application" className="section bg-gray-50/50 backdrop-blur-sm">
                <div className="container max-w-3xl">
                    <button 
                        onClick={handleBackClick}
                        className="mb-8 flex items-center text-green-600 hover:text-green-500 transition-colors back-button"
                    >
                        <i className="fas fa-arrow-left mr-2"></i> Back to Volunteer Opportunities
                    </button>

                    <div className="bg-white/90 backdrop-blur-md rounded-xl shadow-xl p-8 transform transition-all duration-500 hover:shadow-2xl">
                        {submitStatus === 'success' ? (
                            <div className="text-center p-8">
                                <div className="text-green-500 text-5xl mb-4">
                                    <i className="fas fa-check-circle"></i>
                                </div>
                                <h3 className="text-2xl font-bold mb-2">Application Submitted!</h3>
                                <p className="text-gray-600 mb-6">
                                    Thank you for your interest in volunteering with us. We'll review your application and get back to you within 3 business days.
                                </p>
                                <button
                                    onClick={() => setSubmitStatus(null)}
                                    className="btn btn-primary"
                                >
                                    Submit Another Application
                                </button>
                            </div>
                        ) : (
                            <form data-name="volunteer-form" onSubmit={handleSubmit}>
                                <div className="text-center mb-8">
                                    <h2 className="text-3xl font-bold text-gray-800">
                                        Volunteer <span className="text-green-600">Application</span>
                                    </h2>
                                    <p className="text-gray-600 mt-2">
                                        Help us transform e-waste into opportunities for education and sustainability
                                    </p>
                                </div>

                                <div className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="form-group">
                                            <label className="block text-gray-700 mb-2 font-medium">
                                                Full Name <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-800"
                                                required
                                                style={{ color: '#374151', backgroundColor: '#FFFFFF' }}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="block text-gray-700 mb-2 font-medium">
                                                Email <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-800"
                                                required
                                                style={{ color: '#374151', backgroundColor: '#FFFFFF' }}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="form-group">
                                            <label className="block text-gray-700 mb-2 font-medium">
                                                Phone Number
                                            </label>
                                            <input
                                                type="tel"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-800"
                                                style={{ color: '#374151', backgroundColor: '#FFFFFF' }}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="block text-gray-700 mb-2 font-medium">
                                                Volunteer Role <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                name="role"
                                                value={formData.role}
                                                onChange={handleChange}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-800"
                                                required
                                                style={{ color: '#374151', backgroundColor: '#FFFFFF' }}
                                            >
                                                <option value="">Select a role</option>
                                                {volunteerRoles.map((role) => (
                                                    <option key={role.value} value={role.value}>
                                                        {role.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label className="block text-gray-700 mb-2 font-medium">
                                            Relevant Skills <span className="text-red-500">*</span>
                                        </label>
                                        <textarea
                                            name="skills"
                                            value={formData.skills}
                                            onChange={handleChange}
                                            rows="3"
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-800"
                                            placeholder="Technical skills, teaching experience, languages, etc."
                                            required
                                            style={{ color: '#374151', backgroundColor: '#FFFFFF' }}
                                        ></textarea>
                                    </div>

                                    <div className="form-group">
                                        <label className="block text-gray-700 mb-2 font-medium">
                                            Relevant Experience
                                        </label>
                                        <textarea
                                            name="experience"
                                            value={formData.experience}
                                            onChange={handleChange}
                                            rows="3"
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-800"
                                            placeholder="Previous volunteer work, professional experience, etc."
                                            style={{ color: '#374151', backgroundColor: '#FFFFFF' }}
                                        ></textarea>
                                    </div>

                                    <div className="form-group">
                                        <label className="block text-gray-700 mb-2 font-medium">
                                            Availability <span className="text-red-500">*</span>
                                        </label>
                                        <textarea
                                            name="availability"
                                            value={formData.availability}
                                            onChange={handleChange}
                                            rows="2"
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-800"
                                            placeholder="Hours per week, preferred days/times"
                                            required
                                            style={{ color: '#374151', backgroundColor: '#FFFFFF' }}
                                        ></textarea>
                                    </div>

                                    <div className="form-group">
                                        <label className="block text-gray-700 mb-2 font-medium">
                                            Why do you want to volunteer with us? <span className="text-red-500">*</span>
                                        </label>
                                        <textarea
                                            name="motivation"
                                            value={formData.motivation}
                                            onChange={handleChange}
                                            rows="4"
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-800"
                                            required
                                            style={{ color: '#374151', backgroundColor: '#FFFFFF' }}
                                        ></textarea>
                                    </div>

                                    <div className="mt-8">
                                        <button
                                            type="submit"
                                            className="btn btn-primary w-full"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <i className="fas fa-spinner fa-spin mr-2"></i>
                                                    Submitting...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-paper-plane mr-2"></i>
                                                    Submit Application
                                                </>
                                            )}
                                        </button>
                                    </div>

                                    {submitStatus === 'error' && (
                                        <div className="mt-4 text-center text-red-500">
                                            <i className="fas fa-exclamation-circle mr-2"></i>
                                            There was an error submitting your application. Please try again.
                                        </div>
                                    )}
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </section>
        );
    } catch (error) {
        console.error('VolunteerApplication error:', error);
        reportError(error);
        return null;
    }
}

export default VolunteerApplication;
